// src/pages/dashboard/components/AnalyticsHeader.jsx
import { FileDownload as DownloadIcon, FilterList as FilterIcon } from '@mui/icons-material';
import { Button, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
// import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import React, { useEffect, useState } from 'react';
import { getDateRangeForPeriod } from '../../../utils/dateUtils';

// Client and brand data hooks
import useAnalyticsStore from '../../../stores/analyticsStore';
import useAuthStore from '../../../stores/authStore';
import useBrandStore from '../../../stores/brandStore';
import useClientStore from '../../../stores/clientStore';
import { DateRangePicker } from '../../../components/ui';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

// Debug function to help troubleshoot filtering
const logFilters = (label, filters, dateRange) => {
  console.log(`${label}:`, {
    filters,
    dateRange: {
      startDate: dateRange?.startDate?.toISOString(),
      endDate: dateRange?.endDate?.toISOString()
    }
  });
};

const periodOptions = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'custom', label: 'Custom Range' }
];

const AnalyticsHeader = ({ dateRange, onDateRangeChange, filters, onFilterChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down('md') || '(max-width: 960px)');
  const { hasPermission } = useAuthStore();
  
  // Get client and brand data
  const { clients, fetchClients } = useClientStore();
  const { brands, brandsByClient, fetchBrands } = useBrandStore();
  
  // Get export functionality from analytics store
  const { exportAnalyticsData, isLoading: exportLoading } = useAnalyticsStore();
  
  // Local state
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showFilters, setShowFilters] = useState(!isMobile);

  useEffect(()=>{
    //fetch them initially
    fetchClients()
    fetchBrands();

  },[])
  // Handle period change
  const handlePeriodChange = (event) => {
    const value = event.target.value;
    
    // Log before change
    logFilters('Before period change', filters, dateRange);
    
    // If custom, show date picker
    if (value === 'custom') {
      setShowDatePicker(true);
      // Don't change dates yet, let user pick them
    } else {
      setShowDatePicker(false);
      
      // Update date range based on selected period
      const { startDate, endDate } = getDateRangeForPeriod(value);
      
      // Log the new date range
      console.log(`New date range for ${value}:`, {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      
      // Update the date range
      onDateRangeChange(startDate, endDate);
    }
    
    // Update the period filter after date range is set
    onFilterChange('period', value);
    
    // Log after change
    // setTimeout(() => {
    //   logFilters('After period change', 
    //     {...filters, period: value}, 
    //     {startDate, endDate: new Date()}
    //   );
    // }, 0);
  };

  // Handle client change
  const handleClientChange = (event) => {
    const clientId = event.target.value;
    
    // Log before change
    logFilters('Before client change', filters, dateRange);
    
    // Reset brand when client changes
    onFilterChange('brandId', '');
    
    // Update client filter
    onFilterChange('clientId', clientId);
    
    // Log after change
    setTimeout(() => {
      logFilters('After client change', 
        {...filters, clientId, brandId: ''}, 
        dateRange
      );
    }, 0);
  };

  // Handle brand change
  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    
    // Log before change
    logFilters('Before brand change', filters, dateRange);
    
    // Update brand filter
    onFilterChange('brandId', brandId);
    
    // Log after change
    setTimeout(() => {
      logFilters('After brand change', 
        {...filters, brandId}, 
        dateRange
      );
    }, 0);
  };

  // Get available brands based on selected client
  const availableBrands = filters.clientId 
    ? brandsByClient[filters.clientId] || [] 
    : brands;

  return (
    <StyledPaper elevation={0}>
      <Grid container spacing={3}>
        <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            Dashboard Analytics
          </Typography>
          
          {/* Mobile filters toggle */}
          {isMobile && (
            <IconButton 
               
              onClick={() => setShowFilters(!showFilters)}
              sx={{ 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1
              }}
            >
              <FilterIcon />
            </IconButton>
          )}
        </Grid>

        {/* Filters row - conditional rendering for mobile */}
        {showFilters && (
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {/* Time period selector */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Time Period</InputLabel>
                  <Select
                    value={filters.period}
                    label="Time Period"
                    onChange={handlePeriodChange}
                  >
                    {periodOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Date range picker - show only when custom period is selected */}
              {showDatePicker && (
                <Grid item xs={12} sm={6} md={4}>
                  {/* <LocalizationProvider dateAdapter={AdapterDateFns}> */}
                    <DateRangePicker
                      startText="From"
                      endText="To"
                      value={[dateRange.startDate, dateRange.endDate]}
                      onChange={(newValue) => {
                        if (newValue[0] && newValue[1]) {
                          onDateRangeChange(newValue[0], newValue[1]);
                        }
                      }}
                      renderInput={(startProps, endProps) => (
                        <Stack direction="row" spacing={1}>
                          <TextField {...startProps} size="small" />
                          <TextField {...endProps} size="small" />
                        </Stack>
                      )}
                    />
                  {/* </LocalizationProvider> */}
                </Grid>
              )}

              {/* Client selector - show only if user has permission */}
              {hasPermission('clients', 'read') && (
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Client</InputLabel>
                    <Select
                      value={filters.clientId}
                      label="Client"
                      onChange={handleClientChange}
                    >
                      <MenuItem value="">All Clients</MenuItem>
                      {clients.map(client => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Brand selector - show only if user has permission */}
              {hasPermission('brands', 'read') && (
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Brand</InputLabel>
                    <Select
                      value={filters.brandId}
                      label="Brand"
                      onChange={handleBrandChange}
                      disabled={!filters.clientId && clients.length > 0}
                    >
                      <MenuItem value="">All Brands</MenuItem>
                      {availableBrands.map(brand => (
                        <MenuItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Export button - show only if user has permission */}
              {hasPermission('analytics', 'export') && (
                <Grid item xs={12} sm={6} md={1}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<DownloadIcon />}
                    size="medium"
                    sx={{ height: '100%' }}
                    onClick={() => exportAnalyticsData({
                      type: 'dashboard',
                      ...filters,
                      startDate: dateRange.startDate.toISOString().split('T')[0],
                      endDate: dateRange.endDate.toISOString().split('T')[0]
                    })}
                    disabled={exportLoading}
                  >
                    {exportLoading ? 'Exporting...' : 'Export'}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    </StyledPaper>
  );
};

export default AnalyticsHeader;